import { Condition, Input, LayerType } from "./enums";
import axios, { AxiosResponse } from "axios";
import {
    ILayerSearch,
    IAppEndpoint,
    IUserConfig,
    IUserLayer,
    ICustomConfig,
    IEndpoints,
    IPrint,
    IRewrite,
    IUserBasemap,
    ICustomerRow,
    IExport,
    IHead,
    IIdentifyStyles,
    IIdentifyTolerance,
    IImport,
    ISearchGroups,
    IZoomObject,
    IIdentifyOrder
} from "./IUserConfig";
import { getSubLayers } from "./rest";
import { ColorLike } from "ol/colorlike";
import { getUrlParam } from "./utils";

export interface IConfigOLD {
    default: IDefault;
    geoLocation?: ILocation;
    search: ISearch;
    reCaptchaSiteKey: string;
    upload: IUploadConfig;
    print: IPrintConfig;
    email: IEmailConfig;
    geometryService: IGeometryServiceConfig;
}
export interface IDefault {
    center: number[];
    extentSize: number[];
    viewExtent: number[];
    zoom: number;
    minZoom?: number;
    maxZoom?: number;
    projection: string;
    zoomToLevel: number;
    highLightLayerId: string;
    highLightSheetLayerId: string;
    drawingLayerId: string;
    highLightClusterLayerId: string;
    clickedClusterLayerId: string;
    hoverLayerId: string;
    colors?: string[];
}

export interface ITileGrid {
    origin: number[];
    tileSize?: number[];
    resolutions: number[];
}
export interface ILocation {
    geolocationOptions?: {
        maximumAge: number;
        timeout: number;
        enableHighAccuracy: boolean;
    };
}

export interface IIdentify {
    fields: string[];
    conditionalFields: IIdentifyCondition[];
}

export interface IIdentifyCondition {
    field: string;
    value: string | number | boolean;
    condition: Condition;
    showFields: string[];
}

export interface IFormConfig {
    fields: IInputFieldConfig[];
}
export interface IInputFieldConfig {
    field: string;
    title?: string;
    type?: Input;
    autocomplete?: string;
    required?: boolean;
    validationFn?: (value, comparator?: string) => boolean;
    validationErrorMessage?: string;
    validationComparator?: string;
    visibilityDeterminantField?: string;
    info?: string;
    minDateField?: string;
    maxDateField?: string;
    minDate?: string;
    maxDate?: string;
    hasError?: boolean;
    maxlength?: number; // max length for Text or TextArea fields
}
export interface ISearch {
    url: string;
    searchLayers: ISearchLayer[];
}

export interface ISearchLayer {
    layerId: string;
    field: string;
}

export interface IUploadConfig {
    url: string;
}

export interface IPrintConfig {
    endpoint: string;
    url: string;
}

export interface IEmailConfig {
    subject: string;
    body: string;
    url: string;
}
export interface IGeometryServiceConfig {
    endpoint: string;
    url: string;
}
export interface ILayer {
    id: string;
    layerId: string | number;
    hideInToc: boolean;
    hideInSearch?: boolean;
    hideLegend: boolean;
    visibleLayers?: number[];
    type: LayerType;
    opacity?: number;
    identify?: any;
    maxScale?: number;
    minScale?: number;
    visible: boolean;
    expanded?: boolean;
    searchLayers?: ILayerSearch[] | null;
    title: string;
    url: string;
    endpoint: IAppEndpoint;
    layers?: string[];
    version?: string;
    imagetype?: string;
    copyright?: string;
    showMetadataLayers?: (number | string)[];
    showInMapDownloadWidget?: boolean;
    requiresLogin?: boolean;
    spatialReference?: number;
    imageConfig?: IImageConfig[];
    popupTemplate?: any; //__esri.PopupTemplate;
    tooltip?: ITooltip;
    tocOptions?: ITocOptions[];
    vectorStyles?: IVectorLayerStyles; // only for VectorLayers
    cluster?: IClusterConfig;
    declutter?: boolean; // for VectorLayer, allows symbol decluttering
    tileGrid?: ITileGrid; // use it, if layer has custom resolutions, origin, tileSize
}

export interface IClusterConfig {
    distance?: number;
    minDistance?: number;
    clusterMinScale?: number;
    previewOnHover?: boolean;
    enlargeOnHoverFactor?: number;
    clusterStyle?: IClusterStyle;
    hoverAnimation?: IClusterHoverAnimation;
}

export interface IClusterHoverAnimation {
    circularExpand: boolean;
    circularExpandLabelsCircular?: boolean;
    durationMs: number;
    pointRadiusPx?: number;
    hideLabels?: boolean;
    keepCircularOnClick?: boolean;
}

export interface IVectorLayerStyles {
    icon?: string;
    icons?: IMultiIconStyle;
    point?: IGeometryStyle;
    polyline?: IGeometryStyle;
    polygon?: IGeometryStyle;
    text?: ITextStyle;
    labels?: IScaleDepLabel[]; // Openlayers style/Text parameters goes here
}

export interface IScaleDepLabel {
    maxScale: number;
    minScale: number;
    labels: any[];
}
export interface IMultiIconStyle {
    field: string;
    values: { [key: string]: string }[];
}
export interface IGeometryStyle {
    radius?: number;
    fillColor?: ColorLike;
    strokeColor?: ColorLike;
    strokeWidth?: number;
    label?: string;
    highlightStyle?: IGeometryStyle;
}
export interface ITextStyle {
    text: string;
    font?: string;
    overflow?: boolean;
    textSize?: number;
    fillColor?: ColorLike;
    strokeColor?: ColorLike;
    strokeWidth?: number;
    padding?: number[];
    backgroundFill?: any;
}
export interface IClusterStyle {
    textColor?: ColorLike;
    textSize?: number;
    fillColor?: ColorLike;
    strokeColor?: ColorLike;
    strokeWidth?: number;
    radius?: number;
}
export interface IImageConfig {
    id: number;
    apiProviderId: string;
    layerBrowseName: string;
    fileTableName: string;
}

export interface IExtent {
    xmax: number;
    xmin: number;
    ymax: number;
    ymin: number;
    spatialReference: {
        wkid: number;
    };
}

export interface ILod {
    level: number;
    levelValue?: string;
    resolution: number;
    scale: number;
}

export interface IGeneral {
    startingExtent: IExtent;
}

export interface IBasemap {
    title: string;
    id: string;
    parentId: string;
    childMaps: string[];
    opacity: number;
    img: string;
    layers: ILayer[];
}

export interface IWidgets {
    Settings: boolean;
    Search: boolean;
    CoordinateConversion: boolean;
    MapDownload: boolean;
    Sketch: boolean;
    Bookmarks: boolean;
    Print: boolean;
    LayerList: boolean;
    GeometryExport: boolean;
    GeometryImport: boolean;
    SwipeWidgetEnabled: boolean;
    MapOpacityWidgetEnabled: boolean;
    Compass?: boolean;
    Basemaps?: boolean;
    Copyrights?: boolean;
    Language?: boolean;
}

export interface IGeotelpa {
    endpoint: string;
    searchLayerId: string;
    mapDownloadMaxCount: number;
    helpLink: string;
    helpText: string;
    logo?: string;
    logo2?: string;
}

export interface IPopupConfig {
    text: string;
    id: string;
    version: number;
}

export interface IApiConfig {
    id: string;
    endpoint: IAppEndpoint;
    url: string;
    imageDetails: {
        height: number;
        thumbnailHeight: number;
    };
}

export interface ILayerGroup {
    type: LayerType.LayerGroup;
    id: string;
    title: string;
    layers: ILayer[];
    layerIds?: string[];
    showInMapDownloadWidget?: boolean;
    visible?: boolean;
    expanded?: boolean;
    hideInToc?: boolean;
    hideInSearch?: boolean;
}

export interface ITooltipTemplate {
    title: string;
    subtitle?: string;
    content: string;
    backgroundColor: number[] | string;
    textColor: number[] | string;
}

export interface IHighlightSymbol {
    type: string;
    color: number[] | string;
    width: number | string;
    style: string;
}

export interface ITooltip {
    tooltipTemplate: ITooltipTemplate;
    highlightSymbol: IHighlightSymbol;
}

export interface ITocOptions {
    layerId: number;
    icon?: string | string[];
    label?: string | string[];
    hideToggle?: boolean;
}

export interface ILayerState {
    type: string;
    typeName: string;
    rootLayerId: string | null;
    layerId: string;
    id: string;
    title: string;
    expanded: boolean;
    visible: boolean;
    first?: boolean;
    error?: string;
    image?: ILayerImage[];
    shown?: boolean;
    minScale?: number;
    maxScale?: number;
    hideLegend?: boolean;
    layers: string[];
    sublayers?: ILayerState[];
    hideVisibility?: boolean;
    position?: number;
    parentLayerId?: number;
    showInMapDownloadWidget?: boolean;
}

export interface ILayerImage {
    image: string;
    label: string;
}
export type boundrieTypes = "none" | "always" | "iframe";

export interface IConfig {
    popup?: IPopupConfig[];
    widgetOpenByDefault?: string;
    colapseBasemaps?: boundrieTypes;
    colapseMenu?: boundrieTypes;
    colapseCoordinates?: boundrieTypes;
    basemapsCollapseThreshold?: number;
    widgets: IWidgets;
    hideUiToggle?: boolean;
    coordinateFormats: string[];
    language: string;
    allLanguages: string[];
    customerRow?: ICustomerRow;
    head: IHead;
    print: IPrint;
    identifyGeometries: IIdentifyStyles;
    layersIn: IRewrite[];
    resultOut: IRewrite[];
    identifyIgnoreFields: string[];
    identifyTolerance?: IIdentifyTolerance;
    identifyOrder?: IIdentifyOrder[];
    location?: ILocation;
    endpoints: IEndpoints;
    layerGroups?: ILayerGroup[];
    layers: ILayer[];
    tooltips?: ITooltip[];
    search?: ISearchGroups;
    basemaps: IBasemap[];
    startingExtent: IExtent;
    mapLods?: ILod[];
    export?: IExport;
    import?: IImport;
    geotelpa: IGeotelpa;
    zoomObjects?: { [zoomObject: string]: IZoomObject };
    apiConfig?: IApiConfig[];
    dataDownloadUrl?: string;
}

let layerPositions: string[];
let endpointCache;
let configCache: IConfig;

export const proxyUrl = process.env.NODE_ENV === "development" ? "https://lvmkartes.lvm.lv/pubevents/beta/" : "";
export const all = "Izvēlēties vērtību";
export const defaultOption = { name: all, code: undefined };

export const getConfig = () => loadEndpoints().then((endpoints) => loadConfig(endpoints));

const loadEndpoints = async () => {
    if (endpointCache) {
        return Promise.resolve(endpointCache);
    }
    endpointCache = await axios.get("config/endpoints.json?t=" + new Date().getTime()).then((resp) => resp.data);
    return endpointCache;
};

const loadConfig = async (endpoints) => {
    if (configCache) {
        return Promise.resolve(configCache);
    }
    configCache = await loadConfigJson().then((resp) => {
        console.log("configJSON", resp.data);
        return parseConfig(resp, endpoints).then((c) => c);
    });
    return configCache;
};

const loadConfigJson = async () => axios.get("config/config.json?t=" + new Date().getTime());

const parseConfig = async (resp, endpoints) => {
    let userConfig = resp.data as IUserConfig;
    const customConfig = getUrlParam("config");
    if (customConfig) {
        userConfig = useCustomConfig(customConfig, userConfig);
    }
    const layers = [];
    const layerGroups: ILayerGroup[] = [];
    layerPositions = [];
    for (const layer of userConfig.layers) {
        if (
            layer.type === LayerType.LayerGroup &&
            layer.layers &&
            layer.layers.length &&
            (layer.visible === undefined || layer.visible)
        ) {
            layerGroups.push({
                type: LayerType.LayerGroup,
                id: layer.id,
                title: layer.title,
                visible: layer.visible,
                expanded: layer.expanded,
                hideInToc: layer.hideInToc,
                hideInSearch: layer.hideInSearch,
                layerIds: (layer.layers as IUserLayer[]).map((l) => l.id),
                layers: [],
                showInMapDownloadWidget: layer.showInMapDownloadWidget
            });
            layers.push(...layer.layers);
            if (!layer.hideInToc) {
                layerPositions.push(layer.id);
            }
        }
        if (layer.type !== LayerType.LayerGroup) {
            layers.push(layer);
            if (!layer.hideInToc) {
                layerPositions.push(layer.id);
            }
        }
    }
    if (userConfig.search && userConfig.search.groups) {
        userConfig.search.groups = userConfig.search.groups.filter(
            (group) =>
                group.services.filter((service) => {
                    const layerConfig = userConfig.layers.find((l) => {
                        if (l.type === "LayerGroup" && l.layers?.length) {
                            return (l.layers as IUserLayer[]).find((subLayer) => subLayer.id === service.serviceId);
                        }
                        return l.id === service.serviceId;
                    });

                    return layerConfig && !layerConfig.hideInSearch;
                }).length
        );
    }

    const layersParsed = await Promise.all(
        layers.map(async (layer): Promise<ILayer> => await mapUserLayerToLayer(endpoints, layer as IUserLayer))
    );

    const basemapsParsed = await Promise.all(
        userConfig.basemaps.map(
            async (basemap): Promise<IBasemap> => await mapUserBasemapToBasemap(endpoints, basemap, userConfig.basemaps)
        )
    );

    const config: IConfig = {
        ...userConfig,
        language: getLanguage(userConfig.language) as string,
        print: getPrint(endpoints, userConfig.print),
        layersIn: getPrintRewrite(endpoints, userConfig.layersIn),
        resultOut: getPrintRewrite(endpoints, userConfig.resultOut),
        endpoints,
        apiConfig: userConfig.apiConfig && getApiConfig(endpoints, userConfig.apiConfig),
        mapLods: userConfig.mapLods ? userConfig.mapLods : undefined,
        zoomObjects: userConfig.zoomObjects ? userConfig.zoomObjects : undefined,
        layerGroups,
        layers: layersParsed,
        basemaps: basemapsParsed
    };

    return config;
};

export const getLayerPositions = () => {
    return layerPositions;
};

const getEndpointUrl = (endpoints: IEndpoints, endpoint: IAppEndpoint): string => {
    return endpoints[endpoint.endpoint] + endpoint.url;
};

const getLanguage = (configLanguage: string) => {
    const lang = getUrlParam("lang");
    return lang ? lang.toLowerCase() : configLanguage ? configLanguage : "en";
};

const useCustomConfig = (customConfigName: string, userConfig: IUserConfig) => {
    if (!userConfig.customConfigs) {
        return userConfig;
    }
    const customConfig = userConfig.customConfigs.find(
        (config: ICustomConfig) => config.name.toLowerCase() === customConfigName.toLowerCase()
    );
    if (!customConfig) {
        return;
    }
    if (customConfig.basemaps) {
        customConfig.basemaps.forEach((configBaseMap) => {
            configBaseMap.visible === false &&
                (userConfig.basemaps = userConfig.basemaps.filter((basemap) => basemap.id !== configBaseMap.id));
        });
    }
    if (customConfig.layers) {
        customConfig.layers.forEach((configLayer) => {
            userConfig.layers.forEach((layer) => {
                if (layer.id === configLayer.id) {
                    layer.hideInToc = configLayer.hideInToc !== undefined ? configLayer.hideInToc : layer.hideInToc;
                    layer.hideInSearch =
                        configLayer.hideInSearch !== undefined ? configLayer.hideInSearch : layer.hideInSearch;
                    layer.hideLegend = configLayer.hideLegend !== undefined ? configLayer.hideLegend : layer.hideLegend;
                    layer.visible = configLayer.visible !== undefined ? configLayer.visible : layer.visible;
                    layer.opacity = configLayer.opacity !== undefined ? configLayer.opacity : layer.opacity;
                    layer.identify = configLayer.identify !== undefined ? configLayer.identify : layer.identify;
                    layer.visibleLayers =
                        configLayer.visibleLayers !== undefined ? configLayer.visibleLayers : layer.visibleLayers;
                }
            });
        });
    }
    if (customConfig.widgets) {
        for (const [key, value] of Object.entries(customConfig.widgets)) {
            userConfig.widgets[key] && (userConfig.widgets[key] = value);
        }
    }
    if (customConfig.search) {
        customConfig.search.forEach((configSearch) => {
            configSearch.use === false &&
                (userConfig.search.groups = userConfig.search.groups.filter((group) => group.id !== configSearch.id));
        });
    }
    return userConfig;
};

const mapUserLayerToLayer = async (endpoints: IEndpoints, layer: IUserLayer): Promise<ILayer> => {
    const url = layer.type !== LayerType.LayerGroup && getEndpointUrl(endpoints, layer.endpoint);

    const userLayer = {
        ...layer,
        opacity: layer.opacity || 1,
        url,
        searchLayers: layer.searchLayers ? layer.searchLayers : null,
        tileGrid: layer.tileGrid
    } as ILayer;

    if (layer.type === LayerType.MapImageLayer || layer.type === LayerType.TileLayer) {
        const subLayers = await getSubLayers(url);

        if (subLayers && subLayers.layers) {
            const mainLayers = subLayers.layers.filter((layer) => layer.parentLayerId === -1);
            mainLayers && fillSubLayers(mainLayers, subLayers.layers);

            userLayer.layers = mainLayers;
            userLayer.copyright = subLayers.copyrightText;
        }
    }

    return userLayer;
};

const fillSubLayers = (parentLayers, allLayers) => {
    const parents = Array.isArray(parentLayers) ? parentLayers : [parentLayers];
    return (
        parents &&
        parents.map(
            (parent) =>
                (parent.sublayers = allLayers
                    .filter((layer) =>
                        layer.parentLayerId === parent.id
                            ? layer.subLayerIds && layer.subLayerIds.length
                                ? fillSubLayers(layer, allLayers)
                                : true
                            : false
                    )
                    .reverse())
        )
    );
};

const getApiConfig = (endpoints: IEndpoints, apiConfig: IApiConfig[]) => {
    apiConfig.forEach((api) => (api.url = getEndpointUrl(endpoints, api.endpoint)));
    return apiConfig;
};

const getPrint = (endpoints: IEndpoints, print: IPrint): IPrint => ({
    ...print,
    printDPI: 96,
    url: getEndpointUrl(endpoints, print.endpoint)
});

const getPrintRewrite = (endpoints: IEndpoints, rewrite: IRewrite[]): IRewrite[] => {
    const printRewrites: IRewrite[] = [];
    rewrite.forEach((element: IRewrite) => {
        printRewrites.push({
            fromEndpoint: element.fromEndpoint,
            toEndpoint: element.toEndpoint,
            from: getEndpointUrl(endpoints, element.fromEndpoint),
            to: getEndpointUrl(endpoints, element.toEndpoint)
        });
    });
    return printRewrites;
};

const getChildMapsList = (basemaps: IUserBasemap[], basemap: IUserBasemap) => {
    const childMapsList = basemaps.map((bmp): string => bmp.parent_id === basemap.id && bmp.id).filter((val) => val);
    if (childMapsList.length > 0) {
        childMapsList.push(basemap.id);
        childMapsList.reverse();
        return childMapsList;
    }
    return [];
};

const mapUserBasemapToBasemap = async (endpoints: IEndpoints, basemap: IUserBasemap, basemaps: IUserBasemap[]) => {
    const layersParsed = await Promise.all(
        basemap.layers.map(async (layer): Promise<ILayer> => await mapUserLayerToLayer(endpoints, layer as IUserLayer))
    );
    return {
        ...basemap,
        opacity: basemap.opacity || 1,
        parentId: basemap.parent_id || "",
        childMaps: getChildMapsList(basemaps, basemap),
        url: basemap.layers[0] ? endpoints[basemap.layers[0].endpoint.endpoint] + basemap.layers[0].endpoint.url : "",
        type: basemap.layers[0]?.type,
        layers: layersParsed,
        projection: "EPSG:" + (basemap.layers[0]?.spatialReference || 3059),
        crossOrigin: "anonymous",
        params: {
            LAYERS: basemap.layers[0]?.layers && basemap.layers[0]?.layers[0],
            FORMAT: basemap.layers[0]?.imagetype,
            transparent: false
        },
        serverType: "geoserver",
        minScale: basemap.layers[0]?.minScale,
        maxScale: basemap.layers[0]?.maxScale,
        maxLodLevel: basemap.layers[0]?.maxLodLevel,
        isBaseMap: true,
        copyright: layersParsed[0]?.copyright
    };
};

export const mapTileGrid = {
    origin: [-5120900, 3998100],
    tileSize: [512, 512],
    resolutions: [
        1058.3354500042335, 529.1677250021168, 264.5838625010584, 132.2919312505292, 52.91677250021167,
        26.458386250105836, 13.229193125052918, 10.583354500042335, 7.9375158750317505, 5.291677250021167,
        3.9687579375158752, 2.6458386250105836, 1.3229193125052918, 0.5291677250021167
    ]
};

export const wmtsTileGrid = {
    origin: [189423.1434797966, 449393.55835487787],
    tileSize: [512, 512],
    resolutions: [
        1120, 560, 280, 140, 55.99999999999999, 27.999999999999996, 13.999999999999998, 11.2, 8.399999999999999, 5.6,
        4.199999999999999, 2.8, 1.4, 0.5599999999999999, 0.27999999999999997, 0.21, 0.13999999999999999,
        0.06999999999999999, 0.027999999999999997
    ],
    matrixIds: [
        "LKS_LVM:0",
        "LKS_LVM:1",
        "LKS_LVM:2",
        "LKS_LVM:3",
        "LKS_LVM:4",
        "LKS_LVM:5",
        "LKS_LVM:6",
        "LKS_LVM:7",
        "LKS_LVM:8",
        "LKS_LVM:9",
        "LKS_LVM:10",
        "LKS_LVM:11",
        "LKS_LVM:12",
        "LKS_LVM:13",
        "LKS_LVM:14",
        "LKS_LVM:15",
        "LKS_LVM:16",
        "LKS_LVM:17",
        "LKS_LVM:18"
    ],
    sizes: [
        [1, 1],
        [3, 1],
        [5, 2],
        [9, 4],
        [21, 10],
        [41, 20],
        [81, 40],
        [101, 49],
        [135, 66],
        [202, 98],
        [269, 131],
        [403, 196],
        [806, 391],
        [2014, 977],
        [4027, 1954],
        [5369, 2605],
        [8053, 3907],
        [16105, 7813],
        [40261, 19531]
    ]
};

export const config: IConfigOLD = {
    default: {
        center: [530000, 310000],
        extentSize: [530000, 310000],
        // viewExtent: [160000, 40000, 900000, 570000],
        viewExtent: [-700000, -400000, 2000000, 1000000],
        zoom: 4.5,
        projection: "EPSG:3059",
        zoomToLevel: 13,
        highLightLayerId: "highlightLayer",
        highLightSheetLayerId: "highLightSheetLayerId",
        drawingLayerId: "drawingLayer",
        highLightClusterLayerId: "highLightClusterLayer",
        hoverLayerId: "hoverLayer",
        clickedClusterLayerId: "clickedClusterLayerId",
        colors: [
            "#FF6900",
            "#FCB900",
            "#008744",
            "#00D084",
            "#8ED1FC",
            "#0057e7",
            "#ABB8C3",
            "#333333",
            "#EB144C",
            "#F78DA7",
            "#9900EF"
        ]
    },
    geoLocation: {
        geolocationOptions: {
            maximumAge: 0,
            timeout: 15000,
            enableHighAccuracy: true
        }
    },
    search: {
        url: proxyUrl + "proxy/b3bab3192ed5477d8854f4eea8601f7f",
        searchLayers: [
            { layerId: "0", field: "MUNICIPALITYNAME" },
            { layerId: "1", field: "REGIONNAME" }
        ]
    },
    reCaptchaSiteKey: "6Ld_N5seAAAAAPVbnoGMwILxB_hVXPY32mu90RpQ",
    upload: {
        url: proxyUrl + "proxy/fp_save_67312ef2b5244f28a6183e2397755e88/api/Save"
    },
    print: {
        endpoint: "/print",
        url: proxyUrl + "proxy/print_846d8f58c3da4f91bc914f7fca62d0ec"
    },
    email: {
        subject: "Paziņojums par AS “Latvijas valsts meži” iesniegtu pieteikumu",
        body: "AS “Latvijas valsts meži” ir saņemts Jūsu iesniegtais pieteikums par aktivitātes vai pasākuma saskaņošanu. Par saskaņojumu pasākuma norisei ar Jums sazināsies attiecīgā reģiona Nekustamo īpašumu pārvaldes Zemes lietojuma speciālists.",
        url: proxyUrl + "proxy/email_4f7ad5fa1c62484cab58d7d64013f4aa/api/send"
    },
    geometryService: {
        endpoint: "/api/geometryservice/intersect",
        url: proxyUrl + "proxy/geometry_1daf82b0fba14e20935e972913ff683b"
    }
};
